// Client-safe email typo detector (Mailcheck-style).
//
// `suggestEmail(input)` returns a corrected address when the user's input is
// one or two edits away from a known consumer/ISP mail domain (and not an
// exact match). Returns null otherwise. The intent is to surface a "Did you
// mean ...?" hint before submit; the user stays in control.

import { levenshteinDistance } from "@/lib/search-utils"

const POPULAR_DOMAINS = [
  // Big consumer providers
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.ca",
  "ymail.com",
  "rocketmail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
  "pm.me",
  "duck.com",
  "zoho.com",
  "fastmail.com",
  // US ISPs we still see at signup
  "comcast.net",
  "verizon.net",
  "att.net",
  "sbcglobal.net",
  "bellsouth.net",
  "charter.net",
  "cox.net",
  "earthlink.net",
  "optonline.net",
  "frontier.com",
] as const

const POPULAR_TLDS = [
  "com",
  "net",
  "org",
  "edu",
  "gov",
  "co",
  "io",
  "us",
  "uk",
  "ca",
  "au",
  "de",
  "fr",
  "co.uk",
] as const

// Threshold tuning. Domain part allows up to 2 edits because keyboard typos
// cluster around adjacent keys (gmial, gnail, gmaol). TLD allows 1 edit only
// — `.cm`, `.co`, `.con`, `.om` for `.com`; .ne → .net etc. Anything beyond
// 1 edit on the TLD risks suggesting `.org` for `.com` which is rarely what
// the user meant.
const DOMAIN_THRESHOLD = 2
const TLD_THRESHOLD = 1

function closestMatch(
  candidate: string,
  pool: readonly string[],
  threshold: number,
): string | null {
  let best: string | null = null
  let bestDistance = threshold + 1
  for (const option of pool) {
    if (option === candidate) return null // exact match — nothing to suggest
    const distance = levenshteinDistance(candidate, option)
    if (distance < bestDistance) {
      best = option
      bestDistance = distance
    }
  }
  return bestDistance <= threshold ? best : null
}

function splitDomain(domain: string): { sld: string; tld: string } | null {
  const parts = domain.split(".")
  if (parts.length < 2) return null
  // Handle two-segment TLDs (co.uk, com.au, etc.) by checking the popular list
  if (parts.length >= 3) {
    const lastTwo = parts.slice(-2).join(".")
    if (POPULAR_TLDS.includes(lastTwo as (typeof POPULAR_TLDS)[number])) {
      return { sld: parts.slice(0, -2).join("."), tld: lastTwo }
    }
  }
  return { sld: parts.slice(0, -1).join("."), tld: parts[parts.length - 1] }
}

export function suggestEmail(input: string): string | null {
  if (!input) return null
  const trimmed = input.trim().toLowerCase()
  const at = trimmed.indexOf("@")
  if (at < 1 || at === trimmed.length - 1) return null
  const local = trimmed.slice(0, at)
  const domain = trimmed.slice(at + 1)

  // Don't propose anything if the domain is already in our known-good list.
  if ((POPULAR_DOMAINS as readonly string[]).includes(domain)) return null

  // Step 1: try full-domain correction first (catches "gmial.com", "yahooo.com",
  // "iclud.com" — common 1-2 edit fat-finger typos against popular providers).
  const fullMatch = closestMatch(domain, POPULAR_DOMAINS, DOMAIN_THRESHOLD)
  if (fullMatch) {
    return `${local}@${fullMatch}`
  }

  // Step 2: if the SLD looks legitimate (matches a known provider exactly), try
  // correcting only the TLD. This catches "gmail.con", "yahoo.cm", "hotmail.co"
  // where the second-level domain is right but the TLD got truncated/typo'd.
  const parts = splitDomain(domain)
  if (!parts) return null
  const sldMatchesKnown = POPULAR_DOMAINS.some(
    (d) => d.split(".")[0] === parts.sld,
  )
  if (sldMatchesKnown) {
    const tldMatch = closestMatch(parts.tld, POPULAR_TLDS, TLD_THRESHOLD)
    if (tldMatch && tldMatch !== parts.tld) {
      const candidate = `${parts.sld}.${tldMatch}`
      // Don't suggest a TLD-fix that lands on something not in popular domains
      // when the SLD is a popular-provider SLD — that prevents `gmail.org`.
      if (POPULAR_DOMAINS.includes(candidate as (typeof POPULAR_DOMAINS)[number])) {
        return `${local}@${candidate}`
      }
    }
  }

  return null
}
